import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate, useParams } from '@tanstack/react-router';
import { ArrowLeft, Info, Lock, Pencil, Trash2, X } from 'lucide-react';
import {
  Badge,
  Button,
  Card,
  Checkbox,
  Combobox,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Separator,
  Skeleton,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  Textarea,
  notify,
} from '@noissue/os-ui';
import { adminApi, type AccessInput, type AdminGroupDetail, type AdminModule } from './admin-api';
import { ApiError } from '../api';
import { ACCESS_CONTROL_PATH, userPath } from './paths';

/** Sentinel for "no level" — Select can't hold an empty-string value. */
const NO_LEVEL = '__none__';
/** Sentinel for a held permission set matching no level. */
const CUSTOM = '__custom__';

export function GroupDetailPage() {
  // Dynamically-registered route, so params are read non-strictly (repo idiom).
  const { groupId = '' } = useParams({ strict: false }) as { groupId?: string };
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [editing, setEditing] = useState(false);

  const group = useQuery({
    queryKey: ['admin', 'group', groupId],
    queryFn: () => adminApi.group(groupId),
  });
  const modules = useQuery({ queryKey: ['admin', 'modules'], queryFn: adminApi.modules });

  const invalidate = () => {
    void qc.invalidateQueries({ queryKey: ['admin', 'group', groupId] });
    void qc.invalidateQueries({ queryKey: ['admin', 'groups'] });
  };

  const remove = useMutation({
    mutationFn: () => adminApi.deleteGroup(groupId),
    onSuccess: () => {
      notify.success('Group deleted.');
      void qc.invalidateQueries({ queryKey: ['admin', 'groups'] });
      void navigate({ to: ACCESS_CONTROL_PATH });
    },
    onError: (e) => notify.error(e instanceof ApiError ? e.message : 'Could not delete the group.'),
  });

  if (group.isPending || modules.isPending) {
    return (
      <div className="space-y-4 p-6">
        <Skeleton className="h-8 w-64" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }
  if (group.isError || !group.data || !modules.data) {
    return (
      <div className="p-6">
        <Card className="p-6 text-sm text-destructive">
          Couldn&rsquo;t load this group. {(group.error as Error | null)?.message}
        </Card>
      </div>
    );
  }

  const g = group.data;

  return (
    <div className="space-y-6 p-6">
      <div>
        <Link
          to={ACCESS_CONTROL_PATH}
          className="mb-3 inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft className="size-4" /> Access control
        </Link>
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <div className="flex items-center gap-2">
              <h1 className="text-2xl font-semibold tracking-tight">{g.name}</h1>
              {g.system && (
                <Badge variant="outline" className="gap-1">
                  <Lock className="size-3" /> Built-in
                </Badge>
              )}
              {g.source === 'workspace' && <Badge variant="outline">Workspace</Badge>}
            </div>
            {g.description && (
              <p className="mt-1 text-sm text-muted-foreground">{g.description}</p>
            )}
          </div>
          {/* Built-in groups are re-seeded on every deploy, so the server refuses to
              rename them and offering the control would be a dead end. */}
          {!g.system && (
            <div className="flex flex-wrap items-center gap-2">
              <Button variant="outline" onClick={() => setEditing(true)}>
                <Pencil className="size-4" /> Edit details
              </Button>
              <Button
                variant="outline"
                className="text-destructive"
                onClick={() => remove.mutate()}
                disabled={remove.isPending}
              >
                <Trash2 className="size-4" /> Delete group
              </Button>
            </div>
          )}
        </div>
      </div>

      {/* A rename invalidates the whole admin prefix, not just this group: the name is
          denormalized into the people list and each person's access view (`viaGroups`),
          which would otherwise keep showing the old one until refetched. */}
      <EditGroupDialog
        group={g}
        open={editing}
        onOpenChange={setEditing}
        onSaved={() => void qc.invalidateQueries({ queryKey: ['admin'] })}
      />

      <Tabs defaultValue="access">
        <TabsList>
          <TabsTrigger value="access">Access</TabsTrigger>
          <TabsTrigger value="members">Members ({g.members.length})</TabsTrigger>
        </TabsList>

        <TabsContent value="access" className="pt-4">
          <AccessEditor
            groupId={g.id}
            modules={modules.data}
            current={g.access}
            onSaved={invalidate}
          />
        </TabsContent>

        <TabsContent value="members" className="pt-4">
          <MembersTab
            groupId={g.id}
            members={g.members}
            editable={g.membershipEditable}
            reason={
              g.id === 'local:all-users'
                ? 'Everyone who can sign in is a member automatically, so this list can’t be edited.'
                : 'Membership is mirrored from Google Workspace — edit it there.'
            }
            onChanged={invalidate}
          />
        </TabsContent>
      </Tabs>
    </div>
  );
}

/**
 * Rename a group, and fix its description while you're there — one dialog because
 * they are one PATCH, and a name usually needs the description updated with it.
 *
 * Access and membership are keyed on `group.id`, never the name, so a rename is
 * purely cosmetic: nobody gains or loses anything by it. That is what makes this safe
 * to offer without a confirmation step.
 */
function EditGroupDialog({
  group,
  open,
  onOpenChange,
  onSaved,
}: {
  group: AdminGroupDetail;
  open: boolean;
  onOpenChange: (v: boolean) => void;
  onSaved: () => void;
}) {
  const [name, setName] = useState(group.name);
  const [description, setDescription] = useState(group.description ?? '');

  // Reset the draft whenever the dialog opens, so a cancelled edit doesn't linger
  // into the next one and a rename from another tab isn't overwritten by stale text.
  useEffect(() => {
    if (open) {
      setName(group.name);
      setDescription(group.description ?? '');
    }
  }, [open, group.name, group.description]);

  const trimmed = name.trim();
  const dirty = trimmed !== group.name || description.trim() !== (group.description ?? '');

  const save = useMutation({
    mutationFn: () => adminApi.updateGroup(group.id, { name: trimmed, description }),
    onSuccess: () => {
      notify.success(
        trimmed === group.name ? 'Group updated.' : `Renamed to “${trimmed}”.`,
      );
      onOpenChange(false);
      onSaved();
    },
    // The server rejects duplicate names (case-insensitively) with a message naming
    // the existing group — surface it rather than a generic failure.
    onError: (e) => notify.error(e instanceof ApiError ? e.message : 'Could not update the group.'),
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Edit group</DialogTitle>
          <DialogDescription>
            Renaming changes only what this group is called. Its access and members stay
            exactly as they are.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="edit-group-name">Name</Label>
            <Input
              id="edit-group-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Sales Manager"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="edit-group-description">Description</Label>
            <Textarea
              id="edit-group-description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Optional — who belongs in this group and why."
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={() => save.mutate()} disabled={!trimmed || !dirty || save.isPending}>
            {save.isPending ? 'Saving…' : 'Save changes'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/**
 * One row per module: a level dropdown plus checkboxes for any permissions the module
 * declares outside its ladder (e.g. approving quotes, which is orthogonal to editing
 * rather than more of it).
 */
function AccessEditor({
  groupId,
  modules,
  current,
  onSaved,
}: {
  groupId: string;
  modules: AdminModule[];
  current: { moduleId: string; level: string | null; extras: string[]; custom: boolean }[];
  onSaved: () => void;
}) {
  const initial = useMemo(() => {
    const out: Record<string, { level: string; extras: string[] }> = {};
    for (const m of modules) {
      const held = current.find((c) => c.moduleId === m.id);
      out[m.id] = {
        level: held?.custom ? CUSTOM : (held?.level ?? NO_LEVEL),
        extras: held?.extras ?? [],
      };
    }
    return out;
  }, [modules, current]);

  const [draft, setDraft] = useState(initial);
  // Re-sync when the server data changes under us (after a save, or another tab).
  useEffect(() => setDraft(initial), [initial]);

  const dirty = JSON.stringify(draft) !== JSON.stringify(initial);
  const hasCustom = Object.values(draft).some((d) => d.level === CUSTOM);

  const save = useMutation({
    mutationFn: () => {
      const access: AccessInput[] = modules.map((m) => ({
        moduleId: m.id,
        level: draft[m.id]?.level === NO_LEVEL ? null : (draft[m.id]?.level ?? null),
        extras: draft[m.id]?.extras ?? [],
      }));
      return adminApi.setAccess(groupId, access);
    },
    onSuccess: () => {
      notify.success('Access updated.');
      onSaved();
    },
    onError: (e) => notify.error(e instanceof ApiError ? e.message : 'Could not save access.'),
  });

  const set = (moduleId: string, patch: Partial<{ level: string; extras: string[] }>) =>
    setDraft((d) => ({ ...d, [moduleId]: { ...d[moduleId]!, ...patch } }));

  return (
    <div className="space-y-4">
      <Card className="divide-y p-0">
        {modules.map((m) => {
          const state = draft[m.id]!;
          return (
            <div key={m.id} className="flex flex-wrap items-start justify-between gap-4 p-4">
              <div className="min-w-0">
                <div className="font-medium">{m.name}</div>
                {m.offLadder.length > 0 && (
                  <div className="mt-2 space-y-1.5">
                    {m.offLadder.map((extra) => (
                      <label
                        key={extra.permission}
                        className="flex cursor-pointer items-start gap-2 text-sm"
                      >
                        <Checkbox
                          className="mt-0.5"
                          checked={state.extras.includes(extra.permission)}
                          onCheckedChange={(checked) =>
                            set(m.id, {
                              extras: checked
                                ? [...state.extras, extra.permission]
                                : state.extras.filter((p) => p !== extra.permission),
                            })
                          }
                        />
                        <span>
                          <span className="font-medium">{extra.label}</span>
                          {extra.description && (
                            <span className="block text-xs text-muted-foreground">
                              {extra.description}
                            </span>
                          )}
                        </span>
                      </label>
                    ))}
                  </div>
                )}
              </div>
              <Select value={state.level} onValueChange={(v) => set(m.id, { level: v })}>
                <SelectTrigger className="w-[180px] shrink-0">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={NO_LEVEL}>No access</SelectItem>
                  {m.levels.map((l) => (
                    <SelectItem key={l.key} value={l.key}>
                      {l.label}
                    </SelectItem>
                  ))}
                  {/* Only offered when that's the current state — picking a real level is
                      the way out of it, and it can never be chosen deliberately. */}
                  {state.level === CUSTOM && (
                    <SelectItem value={CUSTOM} disabled>
                      Custom
                    </SelectItem>
                  )}
                </SelectContent>
              </Select>
            </div>
          );
        })}
      </Card>

      {hasCustom && (
        <p className="flex items-start gap-2 text-xs text-muted-foreground">
          <Info className="mt-0.5 size-3.5 shrink-0" />
          One or more modules hold a combination of permissions that matches no level. Choosing
          a level will replace it.
        </p>
      )}

      <div className="flex items-center gap-3">
        <Button onClick={() => save.mutate()} disabled={!dirty || save.isPending}>
          {save.isPending ? 'Saving…' : 'Save access'}
        </Button>
        {dirty && (
          <Button variant="ghost" onClick={() => setDraft(initial)}>
            Discard changes
          </Button>
        )}
      </div>
    </div>
  );
}

function MembersTab({
  groupId,
  members,
  editable,
  reason,
  onChanged,
}: {
  groupId: string;
  members: { id: string; name: string; email: string }[];
  editable: boolean;
  reason: string;
  onChanged: () => void;
}) {
  const users = useQuery({ queryKey: ['admin', 'users'], queryFn: adminApi.users });
  const [selected, setSelected] = useState('');

  const add = useMutation({
    mutationFn: (userId: string) => adminApi.addMember(groupId, userId),
    onSuccess: () => {
      setSelected('');
      onChanged();
    },
    onError: (e) => notify.error(e instanceof ApiError ? e.message : 'Could not add the member.'),
  });
  const remove = useMutation({
    mutationFn: (userId: string) => adminApi.removeMember(groupId, userId),
    onSuccess: onChanged,
    // The server refuses to remove the last person able to manage access control.
    onError: (e) => notify.error(e instanceof ApiError ? e.message : 'Could not remove the member.'),
  });

  const candidates = (users.data ?? [])
    .filter((u) => !members.some((m) => m.id === u.id))
    .map((u) => ({ value: u.id, label: `${u.name} — ${u.email}` }));

  return (
    <div className="space-y-4">
      {!editable ? (
        // Say why rather than rendering controls that would be rejected: All Users
        // membership is rewritten by the seed, so an edit here would silently revert.
        <p className="flex items-start gap-2 text-sm text-muted-foreground">
          <Info className="mt-0.5 size-4 shrink-0" />
          {reason}
        </p>
      ) : (
        <div className="flex flex-wrap items-end gap-2">
          <div className="min-w-[280px] flex-1 space-y-2">
            <Label htmlFor="add-member">Add a member</Label>
            <Combobox
              id="add-member"
              options={candidates}
              value={selected}
              onValueChange={setSelected}
              placeholder="Search people…"
              emptyText="Everyone is already a member."
            />
          </div>
          <Button onClick={() => selected && add.mutate(selected)} disabled={!selected || add.isPending}>
            Add
          </Button>
        </div>
      )}

      <Separator />

      {members.length === 0 ? (
        <p className="py-8 text-center text-sm text-muted-foreground">No members yet.</p>
      ) : (
        <div className="divide-y rounded-md border">
          {members.map((m) => (
            <div key={m.id} className="flex items-center justify-between gap-4 px-4 py-2.5">
              <Link to={userPath(m.id)} className="min-w-0 hover:underline">
                <div className="truncate text-sm font-medium">{m.name}</div>
                <div className="truncate text-xs text-muted-foreground">{m.email}</div>
              </Link>
              {editable && (
                <Button
                  variant="ghost"
                  size="sm"
                  aria-label={`Remove ${m.name}`}
                  onClick={() => remove.mutate(m.id)}
                  disabled={remove.isPending}
                >
                  <X className="size-4" />
                </Button>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
